import { assertNotNull } from '@remote-voice/utilities';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCheckPermission } from '@/components/hooks/useCheckPermission';
import { useSetHeaderTitle } from '@/components/hooks/useHeaderTitle';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import { TemplateForm } from '@/components/molecules/admin/TemplateForm';
import { env } from '@/env';
import {
  File as FileType,
  useAddFilesMutation,
  useAddTemplateMessageMutation,
} from '@/types/graphql';

export const getAudioData = async (
  languages: { message: string; language: string }[],
  ttsSetting: 'Azure' | 'Feat' | 'Mimi',
  voiceRate?: number
) => {
  // TTS設定に基づいてエンドポイントを決定
  const getTtsEndpoint = () => {
    switch (ttsSetting) {
      case 'Azure':
        return env.REACT_APP_MINI_TTS_AZURE_URL;
      case 'Mimi':
        return env.REACT_APP_MINI_TTS_MIMI_URL;
      case 'Feat':
      default:
        // デフォルトはFEAT
        return env.REACT_APP_MIMI_TTS_FEAT_URL;
    }
  };

  // 音声変換
  const audioBinary: {
    lang: string;
    fileName: string;
    fileType: string;
    fileData: File;
  }[] = [];
  for (const lang of languages) {
    const token = ''; // 現在tokenは不要
    const urlParams = new URLSearchParams();
    urlParams.append('text', lang.message);
    urlParams.append('engine', 'nict');
    urlParams.append('lang', lang.language);
    urlParams.append('audio_format', env.REACT_APP_TTS_FORMAT); // WAV or MP3
    urlParams.append('audio_endian', 'Little');
    urlParams.append('gender', 'unknown');
    if (voiceRate != null) {
      urlParams.append('rate', voiceRate.toString());
    }
    const miniBackendId = env.REACT_APP_MINI_BACKEND_ID ?? undefined;
    if (miniBackendId) {
      urlParams.append('backend_id', miniBackendId);
    }
    const result = await axios.post(getTtsEndpoint(), urlParams, {
      headers: {
        Authorization: `Bearer ${token ?? ''}`,
        accept: 'application/json',
        'content-type': 'application/x-www-form-urlencoded',
      },
      responseType: 'arraybuffer',
    });

    const fileName = `tts_${
      lang.language
    }.${env.REACT_APP_TTS_FORMAT.toLowerCase()}`;
    const fileType = `${env.REACT_APP_TTS_FORMAT.toLowerCase()}`;
    const fileData = new File([result.data], fileName, {
      type: `audio/${fileType}`,
    });

    audioBinary.push({
      lang: lang.language,
      fileName,
      fileType,
      fileData,
    });
  }
  return audioBinary;
};

const TemplateAdd = () => {
  useCheckPermission({ tenantCompanyAdmin: true });

  const { t } = useTranslation('admin');
  const { t: commonT } = useTranslation('common');
  useSetHeaderTitle(commonT('breadcrumb.templateAdmin'));

  const [addTemplateMessage, addTemplateMessageResult] =
    useAddTemplateMessageMutation();
  const [addFiles, addFilesResult] = useAddFilesMutation();

  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const location = useLocation();

  const defaultCategoryId = (location.state as { categoryId?: string })
    ?.categoryId;

  return (
    <TemplateForm
      defaultCategoryId={defaultCategoryId}
      onSubmit={async (input, options) => {
        // 添付ファイルを保存
        let uploadedFiles: FileType[] = [];
        const newFiles = input.attachedFiles?.filter(
          (file) => file.fileData != null
        );
        if (newFiles != null && newFiles.length >= 1) {
          const filesResult = await addFiles({
            variables: {
              input: {
                files: newFiles.map((x) => ({
                  fileName: x.fileName,
                  fileType: x.fileType,
                })),
              },
            },
          });
          if (filesResult.errors) throw filesResult.errors;
          const addFilesData = filesResult.data?.addFiles;
          assertNotNull(addFilesData);

          uploadedFiles = await Promise.all(
            addFilesData.map(async (file, i) => {
              // ファイルアップロード
              await axios.put(file.signedURL, newFiles[i].fileData, {
                headers: { 'Content-Type': file.fileType },
              });
              return file;
            })
          );
        }

        const result = await addTemplateMessage({
          variables: {
            input: {
              templateCategoryId: input.templateCategoryId,
              languages: input.languages.map((x) => ({
                language: x.language,
                message: x.message,
                readonly: x.readonly,
              })),
              files: uploadedFiles.map((file) => ({
                id: file.id,
                fileType: file.fileType,
                fileName: file.fileName,
              })),
            },
          },
        });
        if (result.errors) throw result.errors;

        navigate('/admin/template');
        showSnackbar(
          'success',
          t('template.registered') +
            (options.isReplacedBreak ? t('template.replacedBreak') : '')
        );
      }}
      loading={addTemplateMessageResult.loading || addFilesResult.loading}
    />
  );
};
export default TemplateAdd;
